<header class="app-header" [class.is-scrolled]="isScrolled">
  <div class="header-container">

    <!-- LOGO (wordmark matches the official logo colours and stays crisp in dark mode) -->
    <a class="logo" routerLink="/" aria-label="TheNearByShop home">
      <span class="logo-mark" aria-hidden="true">TheNearByShop</span><span class="logo-tld" aria-hidden="true">.com</span>
    </a>

    <!-- LOCATION -->
    <div class="location-block">
      <button type="button" class="location-btn" (click)="openLocationModal()"
        [attr.aria-label]="currentLocation ? 'Delivering to ' + currentLocation.sectorName + '. Change location' : 'Select delivery location'">
        <i class="fas fa-location-dot location-icon" aria-hidden="true"></i>
        <span class="location-text">
          @if (currentLocation) {
            <span class="location-label">
              Deliver to
              @if (deliveryInfo?.available && deliveryInfo?.estimatedTime) {
                <span class="eta-pill"><i class="fas fa-bolt" aria-hidden="true"></i> {{ deliveryInfo?.estimatedTime }}</span>
              } @else if (deliveryInfo?.available === false) {
                <span class="eta-pill is-unavailable">Not deliverable</span>
              }
            </span>
            <span class="location-value">
              {{ currentLocation.sectorName }}@if (currentLocation.addressRest) {<span class="location-rest">, {{ currentLocation.addressRest }}</span>}
            </span>
          } @else {
            <span class="location-label">Deliver to</span>
            <span class="location-value is-muted">Set location</span>
          }
        </span>
        <i class="fas fa-chevron-down location-chevron" aria-hidden="true"></i>
      </button>

      <!-- NEARBY-SHOP SEARCH RADIUS (independent of which address is selected) -->
      <div class="radius-control">
        <button type="button" class="radius-toggle-btn" (click)="toggleRadiusPanel($event)"
          aria-haspopup="dialog" [attr.aria-expanded]="showRadiusPanel" [attr.aria-label]="'Shop search radius ' + radius + ' kilometres'">
          <i class="fas fa-sliders" aria-hidden="true"></i> {{ radius }} km
        </button>

        @if (showRadiusPanel) {
          <div class="radius-popover" (click)="$event.stopPropagation()" role="dialog" aria-label="Search radius">
            <div class="radius-header">
              <span>Show shops within</span>
              <strong>{{ radius }} km</strong>
            </div>
            <input type="range" min="1" max="10" step="1" [(ngModel)]="radius" (change)="onRadiusChange()"
              aria-label="Search radius in kilometres" />
            <div class="range-labels"><span>1 km</span><span>10 km</span></div>
          </div>
        }
      </div>
    </div>

    <!-- SEARCH -->
    <div class="header-search" [class.is-hidden]="onSearchPage">
      <app-search-bar placeholder="Search groceries, medicines, shops…" [value]="searchQuery"
        [hasLiveResults]="searchResults.length > 0" [popularSearches]="popularSearches"
        (queryChange)="onSearchChange($event)" (search)="onSearch($event)" (cleared)="onSearchCleared()">

        <div liveResults class="search-suggestions" role="listbox" aria-label="Product suggestions">
          @for (item of searchResults; track item.id) {
            <button type="button" class="suggestion-item" role="option" (click)="selectResult(item)">
              <img [src]="item.image || 'assets/images/no-image.jpg'" alt="" width="40" height="40" loading="lazy" />
              <span class="suggestion-text">
                <span class="name">{{ item.name }}</span>
                <span class="sub">
                  @if (item.final_price ?? item.price) { ₹{{ item.final_price ?? item.price }} }
                  @if (item.shop?.name) { · {{ item.shop.name }} }
                </span>
              </span>
              <i class="fas fa-arrow-up-right-from-square cta" aria-hidden="true"></i>
            </button>
          }
          <button type="button" class="show-all" (click)="onSearch(searchQuery)">
            See all results for <strong>"{{ searchQuery }}"</strong>
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </app-search-bar>
    </div>

    <!-- ACTIONS -->
    <div class="header-actions">
      <button type="button" class="icon-btn theme-btn" (click)="themeService.toggleTheme()"
        [attr.aria-label]="themeService.theme() === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
        [attr.title]="themeService.theme() === 'dark' ? 'Light theme' : 'Dark theme'">
        <i class="fas" [class.fa-sun]="themeService.theme() === 'dark'" [class.fa-moon]="themeService.theme() !== 'dark'" aria-hidden="true"></i>
      </button>

      <!-- MESSAGES (shown once logged in, with the unread count) -->
      @if (isBrowser && (isLoggedIn$ | async)) {
        <a class="icon-btn msg-btn" routerLink="/chats" routerLinkActive="is-active"
          [attr.aria-label]="chatInbox.unreadTotal() ? 'Messages, ' + chatInbox.unreadTotal() + ' unread' : 'Messages'"
          title="Messages">
          <i class="fas fa-comment-dots" aria-hidden="true"></i>
          @if (chatInbox.unreadTotal()) {
            <span class="count-badge" aria-hidden="true">{{ chatInbox.unreadTotal() > 99 ? '99+' : chatInbox.unreadTotal() }}</span>
          }
        </a>
      }

      <!-- LOGIN / PROFILE (desktop; phones use the bottom nav's Account tab) -->
      <div class="auth-block">
        @if (isBrowser && !(isLoggedIn$ | async)) {
          <button type="button" class="login-btn" (click)="loginSignup()">
            <i class="fas fa-user" aria-hidden="true"></i><span>Login</span>
          </button>
        }

        @if (isBrowser && (isLoggedIn$ | async)) {
          <div class="profile-nav">
            <button type="button" class="profile-trigger" (click)="toggleProfileDropdown($event)" aria-haspopup="menu"
              [attr.aria-expanded]="showProfileDropdown">
              <span class="avatar" aria-hidden="true">{{ firstName.charAt(0) | uppercase }}</span>
              <span class="profile-name">{{ firstName }}</span>
              <i class="fas fa-chevron-down profile-chevron" aria-hidden="true"></i>
            </button>

            @if (showProfileDropdown) {
              <div class="profile-dropdown" (click)="$event.stopPropagation()" role="menu">
                @if (userName) {
                  <div class="dropdown-user">{{ userName }}</div>
                }
                <button type="button" role="menuitem" (click)="goToProfileTab('profile')"><i class="fas fa-user" aria-hidden="true"></i> My profile</button>
                <button type="button" role="menuitem" (click)="goToProfileTab('orders')"><i class="fas fa-box" aria-hidden="true"></i> Orders &amp; tracking</button>
                <button type="button" role="menuitem" (click)="showProfileDropdown = false; router.navigate(['/print/orders'])"><i class="fas fa-print" aria-hidden="true"></i> Print orders</button>
                <button type="button" role="menuitem" (click)="goToProfileTab('subscriptions')"><i class="fas fa-rotate" aria-hidden="true"></i> Subscriptions</button>
                <button type="button" role="menuitem" (click)="goToProfileTab('service-bookings')"><i class="fas fa-calendar-check" aria-hidden="true"></i> Bookings</button>
                <button type="button" role="menuitem" (click)="goToChats()"><i class="fas fa-comment-dots" aria-hidden="true"></i> Messages
                  @if (chatInbox.unreadTotal()) { <span class="menu-badge">{{ chatInbox.unreadTotal() > 99 ? '99+' : chatInbox.unreadTotal() }}</span> }
                </button>
                <button type="button" role="menuitem" (click)="goToProfileTab('wallet')"><i class="fas fa-wallet" aria-hidden="true"></i> Wallet</button>
                <button type="button" role="menuitem" (click)="showProfileDropdown = false; openLocationModal()"><i class="fas fa-location-dot" aria-hidden="true"></i> Saved addresses</button>
                <div class="dropdown-divider" role="separator"></div>
                <button type="button" role="menuitem" class="logout" (click)="logout()"><i class="fas fa-right-from-bracket" aria-hidden="true"></i> Logout</button>
              </div>
            }
          </div>
        }
      </div>

      <!-- CART -->
      <div class="cart-block" (mouseenter)="onCartHoverEnter()" (mouseleave)="onCartHoverLeave()">
        <a class="cart-btn" routerLink="/cart" (click)="onCartClick()"
          [attr.aria-label]="cartCount ? 'Cart, ' + cartCount + ' items' : 'Cart, empty'">
          <span class="cart-icon-wrap">
            <i class="fas fa-cart-shopping" aria-hidden="true"></i>
            @if (cartCount) {
              <span class="cart-badge" aria-hidden="true">{{ cartCount > 99 ? '99+' : cartCount }}</span>
            }
          </span>
          <span class="cart-label">Cart</span>
        </a>

        @if (showCartPreview) {
          <div class="cart-preview" role="region" aria-label="Cart preview">
            @if (cartPreviewLoading) {
              <div class="cart-preview-empty"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading cart…</div>
            } @else if (!cartPreview?.items?.length) {
              <div class="cart-preview-empty">
                <i class="fas fa-basket-shopping" aria-hidden="true"></i>
                <p>Your cart is empty</p>
              </div>
            } @else {
              <ul class="cart-preview-items" role="list">
                @for (item of cartPreview!.items.slice(0, 4); track item.product?.id) {
                  <li class="cart-preview-item">
                    <img [src]="item.product?.image || 'assets/images/no-image.jpg'" alt="" width="40" height="40" loading="lazy" />
                    <span class="cart-preview-item-text">
                      <span class="name">{{ item.product?.name }}</span>
                      <span class="qty">Qty {{ item.quantity }}</span>
                    </span>
                    <span class="price">₹{{ item.price }}</span>
                  </li>
                }
              </ul>
              @if (cartPreview!.items.length > 4) {
                <div class="cart-preview-more">+{{ cartPreview!.items.length - 4 }} more item(s)</div>
              }
              <div class="cart-preview-footer">
                <span class="cart-preview-total">Total <strong>₹{{ cartPreview!.grand_total ?? cartPreview!.total }}</strong></span>
                <a class="ds-btn ds-btn--primary ds-btn--sm" routerLink="/cart" (click)="onCartClick()">View cart</a>
              </div>
            }
          </div>
        }
      </div>
    </div>

  </div>
</header>
